import { detectHost, getDocumentFile, getDocumentId, HOST_LABELS, type OfficeHost } from './host'
import {
  getSelection as getWordSelection,
  getDocumentParagraphsDetailed
} from './word'
import { getSelectedRange } from './excel'
import { getSelectedSlideText } from './powerpoint'
import type { DocSyncSession } from './docSync'

const MAX_SELECTION_CHARS = 4000
const MAX_DOCUMENT_CHARS = 24000

export interface DocSnapshot {
  host: OfficeHost
  hostLabel: string
  /** Active document's file name (basename); undefined when unsaved/unavailable. */
  documentName?: string
  /** Active document's full path/URL; undefined when unsaved/unavailable. */
  documentPath?: string
  /**
   * Stable per-document id embedded in the file (survives rename/move). Used to
   * group chats by document; undefined when the settings API is unavailable.
   */
  documentId?: string
  selectionText?: string
  selectionTruncated?: boolean
  /** Excel only: address of the selected range. */
  selectionAddress?: string
  /**
   * Word only, FALLBACK when the doc-sync mirror is unavailable: document body
   * with each paragraph prefixed by its zero-based index («N» …), truncated.
   */
  documentText?: string
  documentTruncated?: boolean
  /** Word only: total paragraph count (also beyond any truncation cutoff). */
  documentParagraphCount?: number
  /**
   * Word only: when true the full document is mirrored to the AI service and
   * the agent reads it by chunks via docSessionId (read tools).
   */
  documentSynced?: boolean
  docSessionId?: string
  /** Excel only, when synced: one line per sheet (name + dimensions). */
  workbookOutline?: string
}

function truncate(value: string, max = MAX_SELECTION_CHARS): { text: string; truncated: boolean } {
  if (value.length <= max) return { text: value, truncated: false }
  return { text: `${value.slice(0, max)}…`, truncated: true }
}

/**
 * Run one Office.js read; a failure yields undefined instead of throwing so the
 * other reads in the snapshot still land (e.g. a selection read failing must
 * not drop the document text).
 */
async function safeRead<T>(reader: () => Promise<T>): Promise<T | undefined> {
  try {
    return await reader()
  } catch (error) {
    if (import.meta.env.DEV) console.debug('[ramp-ai] snapshot read failed:', error)
    return undefined
  }
}

/**
 * Build a small, low-risk snapshot of the current Office selection so the agent
 * can reason over the active document without us forwarding the whole file.
 * Reads are best-effort and independent — never blocking, never all-or-nothing.
 *
 * For Word the same paragraph read feeds the doc-sync mirror (when a session is
 * provided): with the mirror connected the snapshot stays slim (outline +
 * selection + docSessionId) and the agent pages through the document with its
 * read tools; otherwise the legacy truncated documentText is attached.
 */
export async function buildDocSnapshot(sync?: DocSyncSession): Promise<DocSnapshot> {
  const host = detectHost()
  const snapshot: DocSnapshot = { host, hostLabel: HOST_LABELS[host] }

  const [documentFile, documentId] = await Promise.all([getDocumentFile(), getDocumentId()])
  if (documentFile) {
    snapshot.documentName = documentFile.name
    snapshot.documentPath = documentFile.path
  }
  if (documentId) snapshot.documentId = documentId

  if (host === 'word') {
    const [selectionRaw, paragraphsRaw] = await Promise.all([
      safeRead(getWordSelection),
      safeRead(getDocumentParagraphsDetailed)
    ])
    if (selectionRaw !== undefined) {
      const selection = truncate(selectionRaw)
      snapshot.selectionText = selection.text
      snapshot.selectionTruncated = selection.truncated
    }
    if (paragraphsRaw !== undefined) {
      snapshot.documentParagraphCount = paragraphsRaw.length

      // Keep mirroring even while disconnected — Yjs buffers local updates and
      // the provider replays them on (re)connect.
      try {
        sync?.pushParagraphs(paragraphsRaw)
      } catch (error) {
        if (import.meta.env.DEV) console.debug('[ramp-ai] doc-sync push failed:', error)
      }

      if (sync?.connected) {
        snapshot.documentSynced = true
        snapshot.docSessionId = sync.sessionId
      } else {
        const numbered = paragraphsRaw.map(({ text }, index) => `«${index}» ${text}`).join('\n')
        const document = truncate(numbered, MAX_DOCUMENT_CHARS)
        snapshot.documentText = document.text
        snapshot.documentTruncated = document.truncated
      }
    }
    return snapshot
  }

  if (host === 'excel') {
    const range = await safeRead(getSelectedRange)
    if (range !== undefined) {
      const { text, truncated } = truncate(JSON.stringify(range.values))
      snapshot.selectionText = text
      snapshot.selectionTruncated = truncated
      snapshot.selectionAddress = range.address
    }
    // The workbook mirror is fed by excelSync (content-change events), not by
    // this snapshot — here we only attach the session id and the sheet map.
    if (sync?.connected) {
      const workbookOutline = sync.workbookOutline()
      if (workbookOutline) {
        snapshot.documentSynced = true
        snapshot.docSessionId = sync.sessionId
        snapshot.workbookOutline = workbookOutline
      }
    }
    return snapshot
  }

  if (host === 'powerpoint') {
    const slideText = await safeRead(getSelectedSlideText)
    if (slideText !== undefined) {
      const { text, truncated } = truncate(slideText)
      snapshot.selectionText = text
      snapshot.selectionTruncated = truncated
    }
    return snapshot
  }

  return snapshot
}

/** True when every host read failed — distinct from genuinely empty content (''). */
export function isSnapshotEmpty(snapshot: DocSnapshot): boolean {
  return (
    snapshot.host !== 'unknown' &&
    snapshot.selectionText === undefined &&
    snapshot.documentText === undefined &&
    snapshot.documentParagraphCount === undefined
  )
}
